import { AnimatePresence } from "framer-motion";
import { atom, useAtomValue, useSetAtom } from "jotai";
import React from "react";
import { usePermissionDeniedModal } from "../hooks/usePermissionDeniedModal";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { fmap } from "../utils/fmap";
import AppModal, { AppModalAction, AppModalProps } from "./Generic/AppModal/AppModal";
import { AppOverlay } from "./Generic/AppOverlay";
import PermissionDeniedModal from "./PermissionDeniedModal";

type ModalAction = ((p: { close: () => void }) => AppModalAction) | AppModalAction;

export const ModalActions = {
  Close:
    (options?: { children?: string; onClick?: () => void }) =>
    (props: { close: () => void }): AppModalAction => ({
      key: "close",
      type: "button",
      variant: "link",
      color: "black",
      children: options?.children ?? translate(STRINGS.CLOSE),
      onClick: () => {
        props.close();
        options?.onClick?.();
      },
    }),
};

type ModalInstance = {
  id: string;
  props: AppModalProps;
};

function mapActionsToAppModalActions(params: {
  actions: ModalAction[];
  onRequestClose: () => void;
}): AppModalAction[] {
  const { actions, onRequestClose } = params;

  return actions.map((action) => {
    return typeof action === "function"
      ? action({ close: onRequestClose })
      : {
          ...action,
          onClick:
            fmap(
              action.onClick,
              (onClick) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                onClick(e);
              }
            ) ?? undefined,
        };
  });
}

const modalsAtom = atom<ModalInstance[]>([]);

export type CreatedModalInstance = ReturnType<ReturnType<typeof useModalV2>["createModal"]>;

export function useModalV2() {
  const setModal = useSetAtom(modalsAtom);

  function createModal(params: {
    type: AppModalProps["type"];
    title: AppModalProps["title"];
    id?: string;
    message?: AppModalProps["message"];
    actions?: ModalAction[];
    options?: AppModalProps["options"];
    isLoading?: AppModalProps["isLoading"];
    icon?: AppModalProps["icon"];
    isOpen?: AppModalProps["isOpen"];
  }) {
    const id = params.id ?? Math.random().toString(36).substring(7);
    const modal: ModalInstance = {
      id: id,
      props: {
        ...params,
        ...getDefaultProps({ id, actions: params.actions }),
      },
    };

    setModal((modals) => [...modals, modal]);

    return {
      id,
      remove: () => removeModal(id),
      update: (props: Parameters<typeof updateModal>[1]) => updateModal(id, props),
    };
  }

  function removeModal(id: string) {
    setModal((modals) => modals.filter((modal) => modal.id !== id));
  }

  function setModalProps(id: string, props: Parameters<typeof createModal>["0"]) {
    return updateModal(id, {
      ...getDefaultProps({ id, actions: props.actions }),
      ...props,
    });
  }

  function updateModal(
    id: string,
    props: Partial<{
      type: AppModalProps["type"];
      title: AppModalProps["title"];
      message: AppModalProps["message"];
      actions: ModalAction[];
      isLoading: AppModalProps["isLoading"];
      icon: AppModalProps["icon"];
    }>
  ) {
    setModal((modals) =>
      modals.map((modal) => {
        if (modal.id !== id) {
          return modal;
        }

        const actions =
          props.actions !== undefined
            ? mapActionsToAppModalActions({
                actions: props.actions,
                onRequestClose: () => removeModal(id),
              })
            : modal.props.actions;

        return {
          id,
          props: {
            ...modal.props,
            ...props,
            actions: actions,
          },
        };
      })
    );
  }

  function getDefaultProps(params: {
    id: string;
    actions?: ModalAction[];
  }): Pick<AppModalProps, "isOpen" | "actions" | "onRequestClose"> {
    const actions = mapActionsToAppModalActions({
      actions: params.actions ?? [ModalActions.Close()],
      onRequestClose: () => removeModal(params.id),
    });

    return {
      isOpen: true,
      onRequestClose: () => removeModal(params.id),
      actions,
    };
  }

  return {
    createModal,
    updateModal,
    removeModal,
    setModal: setModalProps,
  };
}

const AppModals = () => {
  const modals = useAtomValue(modalsAtom);
  const permissionDeniedModal = usePermissionDeniedModal();

  return (
    <AppOverlay>
      <AnimatePresence>
        {modals.map((modal) => (
          <AppModal key={modal.id} {...modal.props} withAnimatePresence={false} />
        ))}
        <PermissionDeniedModal
          state={permissionDeniedModal.state}
          onClose={permissionDeniedModal.closeModal}
        />
      </AnimatePresence>
    </AppOverlay>
  );
};

export default React.memo(AppModals);
